/**
 * Bonus Reach — quarterly catch-up callout for clinics currently in the
 * 70–80% monthly utilization band. Tells the OD exactly what avg
 * utilization they need to maintain across the remaining months of the
 * current quarter to still hit the 90% bonus target.
 *
 * Fed by BI snapshot: `utilization.clinics[i].bonus_reach` block. If the
 * clinic is already on-pace (>= 90%) or too far behind (< 70%), the
 * backend returns `bonus_reach: null` and this component renders nothing.
 */
import { Card } from './ui/card';
import { Target, TrendingUp } from 'lucide-react';

export default function BonusReachCallout({ reach, clinicName }) {
  if (!reach) return null;
  const reqPct = Math.round((reach.required_avg_pct || 0) * 1000) / 10;
  const targetPct = Math.round((reach.target_pct || 0.9) * 100);
  const months = reach.months_remaining || 0;
  const monthLabel = months === 1 ? 'month' : 'months';
  const isStretchy = !reach.achievable;
  return (
    <Card
      className={`rounded-2xl border-2 p-4 flex items-start gap-3 ${
        isStretchy
          ? 'border-[#B08A3A]/60 bg-[#B08A3A]'
          : 'border-[#839E97]/45 bg-[#EFF6F3]'
      }`}
      data-testid="bonus-reach-callout"
    >
      <div className={`shrink-0 rounded-full p-2 ${isStretchy ? 'bg-[#B08A3A]/20' : 'bg-[#839E97]/20'}`}>
        <Target size={18} className={isStretchy ? 'text-[#B08A3A]' : 'text-[#839E97]'} />
      </div>
      <div className="flex-1 min-w-0">
        <div className="text-[10px] uppercase tracking-widest font-bold text-[#685B72]">
          Bonus Reach · {reach.quarter_label}
        </div>
        <div className="font-serif text-lg text-[#2D2833] leading-snug mt-0.5">
          {clinicName ? `${clinicName}: y` : 'Y'}ou need{' '}
          <span className={isStretchy ? 'text-[#B08A3A] font-bold' : 'text-[#839E97] font-bold'}>
            {reqPct}% avg
          </span>{' '}
          for the next {months} {monthLabel} to reach the {targetPct}% bonus target.
        </div>
        <div className="text-xs text-[#685B72] mt-1 flex items-center gap-1.5 flex-wrap">
          <TrendingUp size={11} />
          {reach.quarter_shows_todate?.toLocaleString?.() || reach.quarter_shows_todate} shows so far this quarter · need{' '}
          {reach.required_shows_per_month?.toLocaleString?.() || reach.required_shows_per_month} shows/month to close the gap
          {isStretchy && <span className="text-[#B08A3A] font-semibold ml-1">· stretch goal</span>}
        </div>
      </div>
    </Card>
  );
}
